import type { WebinarRoomSubtitlesViewDto } from '@/api/webinarRoom'
import { formatBytes } from '@/utils/fileSize'
import { parseValidationErrors } from '@/utils/errorHandling'
import { parseServerDate } from '@/utils/time'
import { formatMoment, formatRecordingLength } from '@/utils/webinarRoomSetup'
import { SUBTITLE_FILE_EXTENSIONS, SUBTITLE_LANGUAGES, SUBTITLE_MAX_BYTES } from '@/webinarRoom/limits'
import type { SubtitleLanguageCode } from '@/webinarRoom/limits'
import { subtitleLanguageName } from '@/webinarRoom/subtitles/subtitles'

/**
 * What the Recording card's Subtitles section says (ORB-1132). Whether a file is usable is the API's to decide: it
 * reads the file, checks it against the recording's length and answers with the reason it refused, shown as it comes.
 */

export const SUBTITLES_ACCEPT = SUBTITLE_FILE_EXTENSIONS.join(',')

export const SUBTITLE_LANGUAGE_OPTIONS = (Object.entries(SUBTITLE_LANGUAGES) as [SubtitleLanguageCode, string][])
    .map(([value, label]) => ({ value, label }))

/** Why a file can't be sent, or null when it can: `WebValidator.ValidateRoomSubtitles`'s own words, before the upload. */
export function subtitleFileProblem(file: { name: string; size: number }): string | null {
    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase()
    if (!(SUBTITLE_FILE_EXTENSIONS as readonly string[]).includes(extension)) return 'Choose an SRT or VTT file.'
    if (file.size === 0) return 'The file is empty.'
    if (file.size > SUBTITLE_MAX_BYTES) return `A subtitle file can be at most ${formatBytes(SUBTITLE_MAX_BYTES)}.`
    return null
}

/** "northline-q3-cut.srt wasn’t added. It runs to 52:10, but the recording is 45:03. It may be for a different cut." */
export function refusalLine(fileName: string, reason: string | null): string {
    return reason ? `${fileName} wasn’t added. ${reason}` : `${fileName} wasn’t added.`
}

/** The reason in a refused upload's 422, as the API wrote it. */
export function refusalReason(response: { error?: string }): string | null {
    return parseValidationErrors({ error: response.error })[0]?.Message ?? null
}

/** "English · 412 lines · runs to 44:52". */
export function subtitlesFacts(subtitles: WebinarRoomSubtitlesViewDto): string {
    return [
        subtitleLanguageName(subtitles.LanguageCode),
        `${subtitles.LineCount} ${subtitles.LineCount === 1 ? 'line' : 'lines'}`,
        `runs to ${formatRecordingLength(subtitles.EndsAtSeconds)}`,
    ].filter(Boolean).join(' · ')
}

/** "Added by Hyrzl Macabulos, 20 Oct, 11:20 AM", or null when the server did not say who. */
export function subtitlesAddedByLine(subtitles: WebinarRoomSubtitlesViewDto, now: Date): string | null {
    if (!subtitles.UploadedByName) return null
    const at = parseServerDate(subtitles.UploadedUtc)
    return at ? `Added by ${subtitles.UploadedByName}, ${formatMoment(at, now)}` : `Added by ${subtitles.UploadedByName}`
}
